// Content Service - Firebase Firestore with localStorage fallback
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getContent as getFirestoreContent,
  addContent as addFirestoreContent,
  updateContent as updateFirestoreContent,
  deleteContent as deleteFirestoreContent,
  publishContent as publishFirestoreContent,
  unpublishContent as unpublishFirestoreContent,
  incrementContentViews,
  onContentChange,
  type FirestoreContent,
} from './firebase/adminFirestore';
import { isFirebaseConfigured } from './firebase/config';

export type ContentType = 'blog' | 'docs' | 'changelog';
export type ContentStatus = 'published' | 'draft' | 'archived';

export interface ContentItem {
  id: string;
  title: string;
  slug: string;
  type: ContentType;
  status: ContentStatus;
  author: string;
  authorId: string;
  content: string;
  excerpt?: string;
  featuredImage?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  views: number;
  tags: string[];
  metadata?: Record<string, unknown>;
}

interface ContentState {
  items: ContentItem[];
  isLoading: boolean;
  isFirebase: boolean;
  unsubscribe: (() => void) | null;

  // Actions
  initializeFromFirebase: () => Promise<void>;
  subscribeToFirebase: () => void;
  addContent: (item: Omit<ContentItem, 'id' | 'createdAt' | 'updatedAt' | 'views'>) => Promise<ContentItem>;
  updateContent: (id: string, updates: Partial<ContentItem>) => Promise<ContentItem | null>;
  deleteContent: (id: string) => Promise<boolean>;
  getContent: (id: string) => ContentItem | undefined;
  getContentBySlug: (slug: string) => ContentItem | undefined;
  publishContent: (id: string) => Promise<boolean>;
  unpublishContent: (id: string) => Promise<boolean>;
  archiveContent: (id: string) => Promise<boolean>;
  incrementViews: (id: string) => Promise<void>;
  getStats: () => ContentStats;
  getByType: (type: ContentType) => ContentItem[];
  getPublished: () => ContentItem[];
  getDrafts: () => ContentItem[];
}

export interface ContentStats {
  total: number;
  published: number;
  drafts: number;
  archived: number;
  totalViews: number;
  byType: Record<ContentType, number>;
  recentlyUpdated: ContentItem[];
  mostViewed: ContentItem[];
}

// Default content
const defaultContent: ContentItem[] = [
  {
    id: 'cnt-1',
    title: 'Welcome to Ochtopus',
    slug: 'welcome-to-ochtopus',
    type: 'blog',
    status: 'published',
    author: 'Admin',
    authorId: 'usr-1',
    content: '# Welcome to Ochtopus\n\nWe are excited to introduce Ochtopus, the next-generation code editor designed for developers who demand the best.',
    excerpt: 'Introducing Ochtopus - the next-generation code editor for developers.',
    createdAt: '2024-12-01T10:00:00Z',
    updatedAt: '2024-12-15T14:30:00Z',
    publishedAt: '2024-12-01T10:00:00Z',
    views: 4520,
    tags: ['announcement', 'welcome'],
  },
  {
    id: 'cnt-2',
    title: 'Getting Started Guide',
    slug: 'getting-started',
    type: 'docs',
    status: 'published',
    author: 'Admin',
    authorId: 'usr-1',
    content: '# Getting Started with Ochtopus\n\n## Installation\n\nDownload Ochtopus from our website and follow the installation wizard.',
    excerpt: 'Learn how to get started with Ochtopus in minutes.',
    createdAt: '2024-10-10T08:00:00Z',
    updatedAt: '2024-12-20T16:00:00Z',
    publishedAt: '2024-10-15T09:00:00Z',
    views: 12450,
    tags: ['tutorial', 'beginner', 'documentation'],
  },
  {
    id: 'cnt-3',
    title: 'Version 2.0 Release Notes',
    slug: 'v2-0-release-notes',
    type: 'changelog',
    status: 'published',
    author: 'Admin',
    authorId: 'usr-1',
    content: '# Version 2.0 Release Notes\n\n## New Features\n\n- AI Agent integration\n- New extension API\n- Performance improvements',
    excerpt: 'Major release with AI integration and new extension API.',
    createdAt: '2024-12-18T12:00:00Z',
    updatedAt: '2024-12-18T12:00:00Z',
    publishedAt: '2024-12-18T12:00:00Z',
    views: 2340,
    tags: ['release', 'v2.0', 'changelog'],
  },
];

// Convert Firestore content to local format
function firestoreToLocal(content: FirestoreContent): ContentItem {
  return {
    id: content.id,
    title: content.title,
    slug: content.slug,
    type: content.type,
    status: content.status,
    author: content.author,
    authorId: content.authorId,
    content: content.content,
    excerpt: content.excerpt,
    featuredImage: content.featuredImage,
    createdAt: content.createdAt.toISOString(),
    updatedAt: content.updatedAt.toISOString(),
    publishedAt: content.publishedAt?.toISOString(),
    views: content.views,
    tags: content.tags,
  };
}

export const useContentStore = create<ContentState>()(
  persist(
    (set, get) => ({
      items: defaultContent,
      isLoading: false,
      isFirebase: false,
      unsubscribe: null,

      initializeFromFirebase: async () => {
        if (!isFirebaseConfigured()) {
          set({ isFirebase: false });
          return;
        }

        set({ isLoading: true });
        try {
          const firestoreContent = await getFirestoreContent();
          if (firestoreContent.length > 0) {
            const items = firestoreContent.map(firestoreToLocal);
            set({ items, isFirebase: true, isLoading: false });
          } else {
            set({ isFirebase: true, isLoading: false });
          }
        } catch {
          set({ isFirebase: false, isLoading: false });
        }
      },

      subscribeToFirebase: () => {
        if (!isFirebaseConfigured()) return;

        const unsubscribe = onContentChange((firestoreContent) => {
          if (firestoreContent.length > 0) {
            const items = firestoreContent.map(firestoreToLocal);
            set({ items, isFirebase: true });
          }
        });

        set({ unsubscribe });
      },

      addContent: async (itemData) => {
        const now = new Date().toISOString();
        const newItem: ContentItem = {
          ...itemData,
          id: `cnt-${Date.now()}`,
          createdAt: now,
          updatedAt: now,
          views: 0,
        };

        if (isFirebaseConfigured()) {
          try {
            const id = await addFirestoreContent({
              title: itemData.title,
              slug: itemData.slug,
              type: itemData.type,
              status: itemData.status,
              author: itemData.author,
              authorId: itemData.authorId,
              content: itemData.content,
              excerpt: itemData.excerpt,
              featuredImage: itemData.featuredImage,
              tags: itemData.tags,
              publishedAt: itemData.publishedAt ? new Date(itemData.publishedAt) : undefined,
            });
            newItem.id = id;
          } catch {
            // Silent fail
          }
        }

        set((state) => ({
          items: [...state.items, newItem],
        }));

        return newItem;
      },

      updateContent: async (id, updates) => {
        const state = get();
        const index = state.items.findIndex((i) => i.id === id);
        if (index === -1) return null;

        const updatedItem = {
          ...state.items[index],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        const newItems = [...state.items];
        newItems[index] = updatedItem;

        if (isFirebaseConfigured()) {
          try {
            await updateFirestoreContent(id, updates as unknown as Partial<FirestoreContent>);
          } catch {
            // Silent fail
          }
        }

        set({ items: newItems });
        return updatedItem;
      },

      deleteContent: async (id) => {
        if (isFirebaseConfigured()) {
          try {
            await deleteFirestoreContent(id);
          } catch {
            // Silent fail
          }
        }

        set((state) => ({
          items: state.items.filter((i) => i.id !== id),
        }));
        return true;
      },

      getContent: (id) => {
        return get().items.find((i) => i.id === id);
      },

      getContentBySlug: (slug) => {
        return get().items.find((i) => i.slug === slug);
      },

      publishContent: async (id) => {
        if (isFirebaseConfigured()) {
          try {
            await publishFirestoreContent(id);
          } catch {
            // Silent fail
          }
        }

        const result = await get().updateContent(id, {
          status: 'published',
          publishedAt: new Date().toISOString(),
        });
        return result !== null;
      },

      unpublishContent: async (id) => {
        if (isFirebaseConfigured()) {
          try {
            await unpublishFirestoreContent(id);
          } catch {
            // Silent fail
          }
        }

        const result = await get().updateContent(id, {
          status: 'draft',
          publishedAt: undefined,
        });
        return result !== null;
      },

      archiveContent: async (id) => {
        const result = await get().updateContent(id, {
          status: 'archived',
        });
        return result !== null;
      },

      incrementViews: async (id) => {
        const item = get().getContent(id);
        if (item) {
          if (isFirebaseConfigured()) {
            try {
              await incrementContentViews(id);
            } catch {
              // Silent fail
            }
          }
          // Don't update updatedAt for view increments
          const state = get();
          const index = state.items.findIndex((i) => i.id === id);
          if (index !== -1) {
            const newItems = [...state.items];
            newItems[index] = { ...newItems[index], views: newItems[index].views + 1 };
            set({ items: newItems });
          }
        }
      },

      getByType: (type) => {
        return get().items.filter((i) => i.type === type);
      },

      getPublished: () => {
        return get().items.filter((i) => i.status === 'published');
      },

      getDrafts: () => {
        return get().items.filter((i) => i.status === 'draft');
      },

      getStats: () => {
        const items = get().items;

        const stats: ContentStats = {
          total: items.length,
          published: items.filter((i) => i.status === 'published').length,
          drafts: items.filter((i) => i.status === 'draft').length,
          archived: items.filter((i) => i.status === 'archived').length,
          totalViews: items.reduce((sum, i) => sum + i.views, 0),
          byType: {
            blog: items.filter((i) => i.type === 'blog').length,
            docs: items.filter((i) => i.type === 'docs').length,
            changelog: items.filter((i) => i.type === 'changelog').length,
          },
          recentlyUpdated: [...items].sort((a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          ).slice(0, 5),
          mostViewed: [...items].sort((a, b) => b.views - a.views).slice(0, 5),
        };

        return stats;
      },
    }),
    {
      name: 'ochtopus-content',
      partialize: (state) => ({
        items: state.items,
      }),
    }
  )
);

// Helper function to generate slug from title
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

// Initialize Firebase on import
if (typeof window !== 'undefined') {
  useContentStore.getState().initializeFromFirebase();
  useContentStore.getState().subscribeToFirebase();
}
